import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input, InputPassword } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAddAwsApi } from "@/hooks/use-mailbox";
import { extractErrorMessage } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { AWS_REGIONS } from "./aws-stmp";

const formSchema = z.object({
  awsAccessKey: z.string().min(1, "AWS access key is required"),
  awsSecret: z.string().min(1, "AWS secret is required"),
  awsRegion: z.string().min(1, "AWS region is required"),
});

type FormData = z.infer<typeof formSchema>;

interface AmazonApiDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AmazonApiDialog({ open, onOpenChange }: AmazonApiDialogProps) {
  const { mutateAsync: addAwsApi, isPending } = useAddAwsApi();
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      awsAccessKey: "",
      awsSecret: "",
      awsRegion: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      await addAwsApi(data);
      toast.success("Amazon API settings have been successfully configured.");
      onOpenChange(false);
      form.reset();
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "Failed to configure Amazon API settings."
      );
      toast.error(message);
    }
  };

  const onCancel = () => {
    onOpenChange(false);
    form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Amazon API Configuration</DialogTitle>
          <DialogDescription>
            Amazon Simple Email Service (Amazon SES) is a cloud-based email
            sending service designed to help digital marketers and application
            developers send marketing, notification, and transactional emails.
            Obtain your Amazon SES key pairs and fill in the form below to get
            connected.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="awsAccessKey"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>AWS access key *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter your AWS access key"
                      type="text"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="awsSecret"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>AWS secret *</FormLabel>
                  <FormControl>
                    <InputPassword
                      {...field}
                      placeholder="Enter your AWS secret"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="awsRegion"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>AWS region *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select AWS region" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {AWS_REGIONS.map((region) => (
                        <SelectItem key={region.value} value={region.value}>
                          {region.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Alert variant="info" className="rounded-lg">
              <AlertDescription className="text-sm">
                <strong>Note:</strong> We prioritize your privacy. All SMTP
                details are securely stored and fully encrypted to ensure your
                information is protected at all times.
              </AlertDescription>
            </Alert>

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : "Save Configuration"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
